import {
  Button,
  Callout,
  Collapse,
  FormGroup,
  Icon,
  InputGroup,
  Intent,
  Slider,
  Switch,
} from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import {
  SearchPreset,
  SearchRequest,
  SearchResultWithMetadata,
  Target,
  apiContract,
} from '@retro-ui/api-contract';
import {
  estimateSearchTime,
  M1_DEFAULT,
  SearchDetailLevel,
  SearchInvalidTargetStrategy,
  SearchSource,
} from '@retro-ui/shared';
import { ClientInferRequest } from '@ts-rest/core';
import cx from 'classnames';
import { push as changeLocation } from 'connected-react-router';
import _ from 'lodash';
import { ChangeEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { apiClient } from '@f/api-client';
import { ErrorCallout } from '@f/components/errors';
import { Preset } from '@f/components/pages/dashboard/settings/pages/preset/Preset';
import { useSearchParamsValidation } from '@f/components/shared/search-parameter-form/services/useSearchParamsValidation';
import { useConfig, useCreditOverview } from '@f/hooks';
import { pluralize } from '@f/utils/pluralize';
import { CreditCostInfo } from '@f/components/shared/credit-consumption-info/CreditConsumptionInfo';

import { useTargetValidation } from '../hooks/useTargetValidation';
import { calculateCreditConsumption } from '../utils/calculateCreditConsumption';

import { BatchSearchParametersForm, PresetData } from './BatchSearchParametersForm';
import cls from './CreateBatchSearchForm.module.css';
import { TargetInput } from './TargetInput';

type SearchContract = ClientInferRequest<typeof apiContract.search>;

const PRIORITY_LABEL = {
  1: 'Lowest',
  2: 'Low',
  3: 'Low',
  4: 'Normal',
  5: 'Normal',
  6: 'Normal',
  7: 'Normal',
  8: 'High',
  9: 'High',
  10: 'Highest',
} as const;
type PRIORITY_VALUE = keyof typeof PRIORITY_LABEL;

export interface RerunBatch extends SearchRequest {
  presetParams: SearchPreset;
  priority: PRIORITY_VALUE;
  searchParams: {
    presets: SearchPreset;
    detail_level: SearchDetailLevel;
  };
}

interface CreateBatchSearchFormProps {
  rerunBatch: RerunBatch | null;
  rerunBatchResults: SearchResultWithMetadata[] | null;
}

export function CreateBatchSearchForm(props: CreateBatchSearchFormProps) {
  const { rerunBatch, rerunBatchResults } = props;

  const dispatch = useDispatch();

  const presets = useSelector((state: any) => {
    const presets: Preset[] = Object.values(state.reactionSearchPreference.data);
    const presetsWithDisplayName = presets.map(preset => ({
      ...preset,
      displayName: preset.name,
    }));

    if (presetsWithDisplayName.length === 0) {
      return [];
    }

    const systemDefault = {
      ...(presetsWithDisplayName.find(preset => preset.name === M1_DEFAULT) as Preset),
      displayName: '(System default)',
    };
    const userPresets = presetsWithDisplayName.filter(preset => preset.id !== systemDefault.id);

    return [...userPresets, systemDefault];
  });

  const [targets, setTargets] = useState<Target[] | null>(() => {
    if (rerunBatchResults) {
      return rerunBatchResults?.map(result => ({
        smiles: result.targetSmiles,
        name: result.targetMetadata?.name || '',
      }));
    }
    return null;
  });
  const targetValidation = useTargetValidation(targets);

  const [name, setName] = useState(() => {
    if (rerunBatch) {
      const [name] = rerunBatch.name.split(' - Re-run');
      return `${name.trim()} - Re-run ${new Date().toLocaleString()}`;
    }
    return '';
  });

  const [presetData, setPresetData] = useState<PresetData>(() => {
    if (rerunBatch) {
      const presetWasUsed = rerunBatch.presetName != null;
      if (!presetWasUsed) {
        return {
          presetName: null,
          searchParams: rerunBatch.searchParams.presets,
        };
      }
      const existingPreset = presets.find(preset => preset.name === rerunBatch.presetName);
      if (!existingPreset) {
        return {
          presetName: null,
          searchParams: rerunBatch.searchParams.presets,
          warningMessage: 'The preset used to run this search no longer exists.',
        };
      }
      const isExistingPresetDifferent = !_.isEqual(
        existingPreset.searchParams,
        rerunBatch.presetParams
      );
      if (isExistingPresetDifferent) {
        return {
          presetName: null,
          searchParams: rerunBatch.searchParams.presets,
          warningMessage: 'The preset used to run this search changed since running.',
        };
      }
      return {
        presetName: existingPreset.name,
        searchParams: rerunBatch.searchParams.presets,
      };
    }
    const defaultPreset = presets.find(preset => preset.isApiDefault);
    if (defaultPreset) {
      return {
        presetName: defaultPreset.name,
        searchParams: defaultPreset.searchParams,
      };
    }
    const systemDefaultPreset = presets.find(preset => preset.name === M1_DEFAULT);
    return {
      presetName: M1_DEFAULT,
      searchParams: systemDefaultPreset!.searchParams,
    };
  });
  const [priority, setPriority] = useState<PRIORITY_VALUE>(rerunBatch ? rerunBatch.priority : 5);
  const [detailLevel, setDetailLevel] = useState(
    rerunBatch ? rerunBatch.searchParams.detail_level : SearchDetailLevel.ALL_PATHS
  );
  const [moreSettingsOpen, setMoreSettingsOpen] = useState(false);

  const rightPanel = useRef<HTMLDivElement>(null);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [creatingError, setCreatingError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);

  const targetsCount = targets?.filter(target => target.smiles.length > 0)?.length;

  const creditOverview = useCreditOverview();
  const config = useConfig();

  const { models, parallelSearchesCount, coldBootTime } = config.batchSearch;

  const batchSearchConfig = { models, parallelSearchesCount, coldBootTime };

  const estimatedSearchTime = estimateSearchTime(
    batchSearchConfig,
    presetData?.searchParams,
    targetsCount
  );

  const creditsToConsume = calculateCreditConsumption(
    presetData?.searchParams,
    targetsCount,
    models
  );

  const { validation } = useSearchParamsValidation(presetData?.searchParams);

  const canStartBatch = (() => {
    if (presetData == null) {
      return false;
    }
    if (!validation.isValid) {
      return false;
    }
    if (!rerunBatch && targets === null) {
      return false;
    }
    if (
      !creditOverview ||
      !creditsToConsume ||
      BigInt(creditsToConsume) > creditOverview?.balance
    ) {
      return false;
    }
    if (targetValidation.generalError) {
      return false;
    }
    return true;
  })();

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onSearchRun = async () => {
    if (!canStartBatch) {
      return;
    }
    setCreatingError(null);
    setIsCreatingBatch(true);

    const data: SearchContract['runSearch']['body'] = {
      detailLevel,
      invalidTargetStrategy: SearchInvalidTargetStrategy.PASS,
      priority,
      source: SearchSource.UI,
    };
    if (rerunBatch && targets === null) {
      data.rerunBatchId = rerunBatch.id;
    } else {
      const filteredTargets = targets!.filter(target => target.smiles.length > 0);
      data.targets = filteredTargets!.map(({ smiles }) => smiles);
      data.targetsMetadata = filteredTargets!.reduce((metadata, { name }, index) => {
        if (name) {
          metadata[index] = { name };
        }
        return metadata;
      }, {} as Record<string, { name: string }>);
    }
    if (name) {
      data.name = name;
    }
    if (presetData.presetName != null) {
      data.presetName = presetData.presetName;
    } else {
      data.inputParams = presetData.searchParams;
    }

    try {
      const { status, body } = await apiClient.search.runSearch({
        body: data,
      });
      if (status !== 201) {
        throw new Error('Failed to create batch search');
      }
      dispatch(changeLocation(`/dashboard/batch/${body.id}`));
    } catch (error: unknown) {
      setIsCreatingBatch(false);
      setCreatingError(error as string);
    }
  };

  return (
    <div className={cls.container}>
      <div className={cls.leftPanel}>
        {creditOverview && creditOverview.balance <= 0n && (
          <div className="bp4-form-group">
            <Callout intent={Intent.DANGER}>
              Credit limit exceeded (balance is {creditOverview.balance.toString()})
            </Callout>
          </div>
        )}

        <FormGroup
          label="Name"
          labelFor="name"
          className={cls.parameter}
          labelInfo="(optional)"
          helperText="Search name should contain at least 3 characters."
        >
          <InputGroup
            id="name"
            large
            placeholder="Enter name..."
            value={name}
            onChange={onNameChange}
            onBlur={() => {
              setNameError(name.length !== 0 && name.length < 3);
            }}
          />
          {nameError && (
            <Callout intent={Intent.DANGER} className={cls.warning}>
              Search name is too short. It should contain at least 3 characters.
            </Callout>
          )}
        </FormGroup>

        <FormGroup label="Targets" labelInfo="(required)" className={cls.parameter}>
          <TargetInput
            targets={targets}
            targetValidation={targetValidation}
            onChange={setTargets}
          />
        </FormGroup>

        <Switch
          checked={detailLevel === SearchDetailLevel.ALL_PATHS}
          label="Include full retrosynthesis access via SciFinder"
          onChange={() => {
            setDetailLevel(detailLevel =>
              detailLevel === SearchDetailLevel.BEST_PATH
                ? SearchDetailLevel.ALL_PATHS
                : SearchDetailLevel.BEST_PATH
            );
          }}
          className={cls.parameter}
        />

        <Button
          className={cls.moreSettingsButton}
          onClick={() => {
            setIsRightPanelOpen(false);
            setMoreSettingsOpen(!moreSettingsOpen);
          }}
          icon={moreSettingsOpen ? 'chevron-up' : 'chevron-down'}
          rightIcon={!validation.isValid ? 'error' : undefined}
          intent={!validation.isValid ? Intent.DANGER : undefined}
        >
          {moreSettingsOpen ? 'Hide' : 'Edit'} search settings
        </Button>
        <Collapse isOpen={moreSettingsOpen} className={cls.moreSettingsCollapse}>
          <div className={cls.moreSettings}>
            <FormGroup label="Parameter preset" className={cls.parameter}>
              <BatchSearchParametersForm
                presets={presets}
                presetData={presetData}
                onChange={setPresetData}
                searchParamsContainerRef={rightPanel}
                toggleSearchParams={() => setIsRightPanelOpen(!isRightPanelOpen)}
              />
              {presetData.warningMessage && (
                <Callout className={cls.warning} intent={Intent.WARNING}>
                  {presetData.warningMessage}
                </Callout>
              )}
            </FormGroup>

            <FormGroup
              label="Priority"
              helperText="Prioritize some jobs over other ones. Higher value translates to higher priority."
              className={cls.parameter}
            >
              <Slider
                className={cls.prioritySlider}
                max={10}
                min={1}
                value={priority}
                labelRenderer={priorityValue => {
                  if (priorityValue === priority || priorityValue === 1 || priorityValue === 10) {
                    return `${priorityValue} ${PRIORITY_LABEL[priorityValue]}`;
                  }
                  return String(priorityValue);
                }}
                onChange={value => {
                  setPriority(value as PRIORITY_VALUE);
                }}
              />
            </FormGroup>
          </div>
        </Collapse>

        <div
          className="bp4-form-group"
          style={{ display: 'flex', flexDirection: 'row', gap: '0.3rem' }}
        >
          {creditOverview && creditsToConsume && (
            <Callout
              intent={
                BigInt(creditsToConsume) <= creditOverview.balance ? Intent.PRIMARY : Intent.DANGER
              }
            >
              <div
                className="bp4-label"
                style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
              >
                Credit cost: <strong>{creditsToConsume}</strong> (out of{' '}
                {creditOverview.balance.toString()} available)
                <CreditCostInfo
                  targetsCount={targetsCount ?? 0}
                  triggerElement={<Icon icon="small-info-sign" />}
                />
              </div>
            </Callout>
          )}

          {estimatedSearchTime && (
            <Callout intent={Intent.PRIMARY} icon="time">
              <div
                className="bp4-label"
                style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
              >
                Estimated time: <strong>{estimatedSearchTime}</strong>
                <Tooltip2
                  content={
                    <>
                      Estimated processing time is provided for reference only and will vary based
                      on factors
                      <br /> such as selected model, length, number of targets and processing
                      progress.
                    </>
                  }
                >
                  <Icon icon="small-info-sign" />
                </Tooltip2>
              </div>
            </Callout>
          )}
        </div>

        <Button
          large
          icon="search-around"
          intent={Intent.PRIMARY}
          disabled={!canStartBatch}
          loading={isCreatingBatch}
          onClick={onSearchRun}
        >
          Start search{' '}
          {targetsCount
            ? `for ${targetsCount} ${pluralize(targetsCount, 'target', 'targets')}`
            : ''}
        </Button>
        {creatingError && <ErrorCallout className={cls.warning} error={creatingError} />}
      </div>
      <div
        ref={rightPanel}
        className={cx(cls.rightPanel, {
          [cls.rightPanelOpen]: isRightPanelOpen,
        })}
      />
    </div>
  );
}
